import { dataFilter, filterByMarket } from 'modules/Prices/helpers';
import { MarketOption } from 'interface';

const getDataFilters = ({
  wine,
  format,
  vintage
}: {
  wine: string | null;
  format: string | null;
  vintage: string | null;
}) => [
  { value: vintage, key: 'vintage', default: [] },
  { value: wine, key: 'wine_name', default: [] },
  { value: format, key: 'bottle_format' }
];

const applyFiltersToData = ({
  data,
  wine,
  vintage,
  format,
  market
}: {
  data: any;
  wine: string | null;
  vintage: string | null;
  format: string | null;
  market: MarketOption | null;
}) => {
  let filteredData = dataFilter(
    data,
    getDataFilters({ wine, format, vintage })
  );
  filteredData = filterByMarket(filteredData, market, 'network_code');
  return filteredData;
};

const transformDataForChart = ({
  data,
  activeRadioButton,
  currency
}: {
  data: any;
  activeRadioButton: string;
  currency: string | null;
}) => {
  // Transform item function
  const transformItemFunction = (item: any) => {
    const useLocalPrice = currency && currency !== item.displayed_currency;
    return {
      ...item,
      name: item.network_code,
      minPrice: {
        icon: 'negative',
        currency: useLocalPrice ? item.local_currency : item.displayed_currency,
        value: useLocalPrice ? item.min_local_price : item.min_price
      },
      value: {
        icon: 'neutral',
        currency: useLocalPrice ? item.local_currency : item.displayed_currency,
        value: useLocalPrice ? item.avg_local_price : item.avg_price
      },
      maxPrice: {
        icon: 'positive',
        currency: useLocalPrice ? item.local_currency : item.displayed_currency,
        value: useLocalPrice ? item.max_local_price : item.max_price
      }
    };
  };

  // Create formatted data
  const formattedData = data.map((set: any) =>
    set.map((item: any) => transformItemFunction(item))
  );

  // Eventually add last year data
  if (activeRadioButton === "Comparer avec l'année n-1" && data.length > 0) {
    formattedData.push(
      data[0].map((item: any) => ({
        // TODO: Find out if this is really used ?
        isLastYear: true,
        ...transformItemFunction(item)
      }))
    );
  }

  // Sort formatted data by names
  formattedData.forEach((set: any) =>
    set.sort((a: any, b: any) => String(a.name).localeCompare(String(b.name)))
  );

  // Create unique sorted list of names
  const setNames = [
    ...new Set(
      formattedData.flatMap((set: any) => set.map((item: any) => item.name))
    )
  ].sort((a, b) => String(a).localeCompare(String(b)));

  // Create list of wines (index of wine name is the same as index of set)
  const wines = formattedData
    .map((set: any) => (set && set.length > 0 ? set[0].wine_name : null))
    .filter((item: any) => item);

  // Fill missing values (prices) with null values
  formattedData.forEach((set: any, setIndex: number) => {
    setNames.forEach((name, index) => {
      if (!set[index] || set[index].name !== name) {
        set.splice(index, 0, {
          name,
          value: 0,
          wine_name: wines[setIndex]
        });
      }
    });
  });
  return formattedData;
};

const getChartData = ({
  data,
  wine,
  vintage,
  format,
  market,
  activeRadioButton,
  currency,
  competitor,
  competitorVintage
}: {
  data: any;
  wine: string | null;
  vintage: string | null;
  format: string | null;
  market: MarketOption | null;
  activeRadioButton: string;
  currency: string | null;
  competitor: string | null;
  competitorVintage?: string | null;
}) => {
  const wineData = applyFiltersToData({
    data,
    wine,
    vintage,
    format,
    market
  });

  const competitorData = competitor
    ? [
        applyFiltersToData({
          data,
          wine: competitor,
          vintage: competitorVintage ? competitorVintage : vintage,
          format,
          market
        })
      ]
    : [];

  return transformDataForChart({
    data: [wineData, ...competitorData],
    activeRadioButton,
    currency
  });
};

export { getDataFilters, getChartData };
